import apiFetch from "./apiFetch";

// ---------------- TALENTS API ----------------

export const talentsAPI = {
  // Register talent
  registerTalent: (talentData: any) => {
    const isFormData = talentData instanceof FormData;
    return apiFetch("/auth/talent-register", {
      method: "POST",
      auth: false,
      body: isFormData ? talentData : JSON.stringify(talentData),
      headers: isFormData ? {} : undefined,
    });
  },

  getContentCreatorTypes: () => {
    return apiFetch("/content_creator_types", {
      method: "GET",
      auth: false,
    });
  },
  // Get all talents
  getAll: (params?: {
    page?: number;
    per_page?: number;
    limit?: number;
    status?: string;
    search?: string;
    // Sidebar filter state, e.g. { gender: ["female"], specialties: ["Dancer"] }.
    // Each entry is sent as a repeated `key[]=value` param so the backend can
    // match against the full dataset instead of only the pages already loaded.
    filters?: Record<string, string[]>;
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set("page", String(params.page));
    if (params?.per_page) query.set("per_page", String(params.per_page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.status) query.set("status", params.status);
    if (params?.search) query.set("search", params.search);
    if (params?.filters) {
      for (const [key, values] of Object.entries(params.filters)) {
        (values ?? []).forEach((v) => query.append(`${key}[]`, v));
      }
    }
    const queryString = query.toString() ? `?${query.toString()}` : "";

    return apiFetch(`/talents${queryString}`, {
      method: "GET",
    });
  },

  // Recommended talents matched server-side against the full talent pool
  // (not just one page) — used by the casting brief form. Every field name
  // here matches TalentController::recommended()'s validated params exactly,
  // which in turn match Talent's own columns (see shared/constants/talentOptions.ts).
  getRecommended: (params: {
    languages?: string[];
    gender?: string;
    skin_tone?: string;
    eye_color?: string;
    hair_color?: string;
    hair_type?: string;
    body_type?: string;
    nationality?: string;
    city?: string;
    look_alike?: string;
    talent_type?: string;
    experience_years?: string;
    age_min?: number;
    age_max?: number;
    height_min?: number;
    height_max?: number;
    weight_min?: number;
    weight_max?: number;
    specialty_ids?: number[];
    specialty_option_ids?: number[];
    exclude?: string[];
    limit?: number;
  }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    (params.languages ?? []).forEach((l) => query.append("languages[]", l));
    (params.specialty_ids ?? []).forEach((id) => query.append("specialty_ids[]", String(id)));
    (params.specialty_option_ids ?? []).forEach((id) => query.append("specialty_option_ids[]", String(id)));
    (params.exclude ?? []).forEach((id) => query.append("exclude[]", id));

    const scalarFields = [
      "gender", "skin_tone", "eye_color", "hair_color", "hair_type", "body_type",
      "nationality", "city", "look_alike", "talent_type", "experience_years",
      "age_min", "age_max", "height_min", "height_max", "weight_min", "weight_max",
      "limit",
    ] as const;
    scalarFields.forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
    });

    return apiFetch(`/talents/recommended?${query.toString()}`, {
      method: "GET",
      signal,
    });
  },

  // Get single talent
  getById: (id: string) => {
    return apiFetch(`/talents/${id}`, {
      method: "GET",
    });
  },

  // Update talent
  update: (id: string, updates: any) => {
    return apiFetch(`/talents/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  },

  // Get talent by authenticated user
  getByUser: () => {
    return apiFetch("/auth/me", {
      method: "GET",
    });
  },

  // Toggle like. Returns { user_liked, likes_count }.
  likeTalent: (talentId: string) => {
    return apiFetch("/talents/like", {
      method: "POST",
      body: JSON.stringify({ talent_id: talentId }),
    });
  },

  // Get specialties (public endpoint)
  getSpecialties: () => {
    return apiFetch("/specialties", {
      method: "GET",
      auth: false,
    });
  },

  // Upload media photos for a talent
  uploadMedia: (id: string, files: File[]) => {
    const fd = new FormData();
    files.forEach((file) => fd.append("images[]", file));
    return apiFetch(`/users/upload-media/${id}`, {
      method: "POST",
      body: fd,
      headers: {},
    });
  },

  // Get media for a user
  getMedia: (userId: string) =>
    apiFetch(`/talents/${userId}`, { method: "GET" }),

  getTalentReport: (userId: string) =>
    apiFetch(`/talent-report/${userId}`, { method: "GET" }),

  // Delete a single media item
  deleteMedia: (mediaId: string | number) =>
    apiFetch(`/media/${mediaId}`, { method: "DELETE" }),

  // Update a user's avatar (self or admin)
  updateProfileImage: (userId: string | number, file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return apiFetch(`/users/${userId}/profile-image`, {
      method: "POST",
      body: fd,
      headers: {},
    });
  },
};
